// src/pages/Reports.tsx
import React, { useMemo, useState, useEffect, useCallback, useRef } from 'react';
import {
  Container, Stack, Paper, Typography, FormControlLabel, Switch,
  Tabs, Tab, Divider, Button, Alert
} from '@mui/material';
import Box from '@mui/material/Box';
import { useLocation } from 'react-router-dom';
import MonthPicker from '../components/reports/MonthPicker';
import MonthlySummaryChart from '../components/reports/MonthlySummaryChart';
import CategorySpendPie from '../components/reports/CategorySpendPie';
import TrendsSparkline from '../components/reports/TrendsSparkline';

import {
  useMonthlyRollups,
  useCategoryBreakdown,
  useTrends,
  useBudgetVsActual,
  useSavingsTrajectory
} from '../hooks/useReports';

import SummaryStrip from '../components/reports/SummaryStrip';
import InsightsList from '../components/reports/InsightsList';
import CategoryTable from '../components/reports/CategoryTable';
import { useFormatting } from '../context/FormattingContext';
import { formatMoney } from '../utils/formatting';
import { buildReportsPdfFilename, exportReportsViewToPdf } from '../utils/reportsPdfExport';

const getDefaultMonth = () => {
  const d = new Date();
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth()+1).padStart(2,'0')}`;
};

const parseInitialMonth = (search: string) => {
  const month = new URLSearchParams(search).get('month');
  if (!month || !/^\d{4}-\d{2}$/.test(month)) return getDefaultMonth();

  const [, rawMonth] = month.split('-').map(Number);
  if (rawMonth < 1 || rawMonth > 12) return getDefaultMonth();

  return month;
};

const parseInitialIncludeTentative = (search: string) => {
  const includeTentative = new URLSearchParams(search).get('includeTentative');
  if (includeTentative === 'true') return true;
  if (includeTentative === 'false') return false;
  return false;
};

const Reports: React.FC = () => {
  useEffect(() => { document.title = 'Reports & Trends | Velora Money'; }, []);
  const location = useLocation();
  const formatting = useFormatting();
  const fmtCurrency = useCallback(
    (v: number) => formatMoney(v, formatting, { maximumFractionDigits: 0 }),
    [formatting]
  );

  const [month, setMonth] = useState<string>(() => parseInitialMonth(location.search));
  const [includeTentative, setIncludeTentative] = useState(() => parseInitialIncludeTentative(location.search));
  const [comparePrev, setComparePrev] = useState(true);
  const [tab, setTab] = useState<'overview'|'categories'|'trends'>('overview');
  const [isExporting, setIsExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);
  const exportRef = useRef<HTMLDivElement | null>(null);

  // Query params (keep semantics consistent with the app)
  const monthParams = useMemo(() => ({
    month,
    includeTentative,
    showFuture: false
  }), [month, includeTentative]);

  const trendsParams = useMemo(() => {
    const [y,m] = month.split('-').map(Number);
    const from = new Date(Date.UTC(y, m-1-11, 1)); // 12 months window
    const to = new Date(Date.UTC(y, m-1, 28));
    const fmt = (d: Date) => `${d.getUTCFullYear()}-${String(d.getUTCMonth()+1).padStart(2,'0')}-${String(d.getUTCDate()).padStart(2,'0')}`;
    return { startDate: fmt(from), endDate: fmt(to), includeTentative, showFuture: false };
  }, [month, includeTentative]);

  const { data: rollups, isLoading: isRollupsLoading } = useMonthlyRollups(monthParams);
  const { data: cat, isLoading: isCatLoading } = useCategoryBreakdown(monthParams);
  const { data: trends, isLoading: isTrendsLoading } = useTrends(trendsParams);
  const { data: bva, isLoading: isBvaLoading } = useBudgetVsActual(monthParams);
  const { data: savings, isLoading: isSavingsLoading } = useSavingsTrajectory(monthParams);

  // Current, previous, last year keys
  const prevKey = useMemo(() => {
    const [y,m] = month.split('-').map(Number);
    const d = new Date(Date.UTC(y, m-2, 1));
    return `${d.getUTCFullYear()}-${String(d.getUTCMonth()+1).padStart(2,'0')}`;
  }, [month]);

  const yoyKey = useMemo(() => {
    const [y,m] = month.split('-').map(Number);
    return `${y-1}-${String(m).padStart(2,'0')}`;
  }, [month]);

  const current = rollups?.find(d => d.month === month);
  const prev    = rollups?.find(d => d.month === prevKey);
  const yoy     = rollups?.find(d => d.month === yoyKey);
  const budget  = bva?.series.find(s => s.month === month);

  // Derived KPIs
  const kpis = useMemo(() => {
    if (!current) return null;
    const sr = current.income > 0 ? (current.savings / current.income) : 0;
    const deltas = (a?: number, b?: number) => {
      if (a === undefined || b === undefined) return undefined;
      if (b === 0) return a === 0 ? 0 : 1; // avoid div by 0; treat as 100% if a>0
      return (a - b) / b;
    };
    return {
      income: { value: current.income, mom: deltas(current.income, prev?.income), yoy: deltas(current.income, yoy?.income) },
      expense:{ value: current.expense, mom: deltas(current.expense, prev?.expense), yoy: deltas(current.expense, yoy?.expense) },
      net:    { value: current.net,     mom: deltas(current.net,     prev?.net),     yoy: deltas(current.net,     yoy?.net) },
      savings:{ value: current.savings,  mom: deltas(current.savings, prev?.savings), yoy: deltas(current.savings, yoy?.savings) },
      savingsRate: { value: sr, mom: deltas(
        current.income>0? current.savings/current.income : 0,
        (prev && prev.income>0) ? prev.savings/prev.income : 0
      )}
    };
  }, [current, prev, yoy]);

  // Insights (simple first pass; can evolve with anomalies endpoint later)
  const insights = useMemo(() => {
    const list: string[] = [];
    if (!current) return list;

    if (budget) {
      const variance = budget.variance; // + = under budget
      if (variance >= 0) list.push(`You are ${fmtCurrency(variance)} under budget in ${month}.`);
      else list.push(`You are ${fmtCurrency(Math.abs(variance))} over budget in ${month}.`);
    }

    if (kpis?.savingsRate?.value !== undefined) {
      const pct = Math.round(kpis.savingsRate.value * 100);
      list.push(`Savings rate: ${pct}% this month.`);
    }

    if (cat?.items?.length) {
      const totalExp = current.expense || 0;
      const top = [...cat.items.filter(i => i.type === 'expense')].sort((a,b)=>b.amount-a.amount)[0];
      if (top && totalExp > 0) {
        const share = Math.round((top.amount/totalExp)*100);
        list.push(`Top expense category: ${top.category} (${share}% of expenses).`);
      }
    }

    if (prev && current.expense > prev.expense) {
      list.push(`Expenses up by ${fmtCurrency(current.expense - prev.expense)} vs previous month.`);
    }
    if (prev && current.income < prev.income) {
      list.push(`Income down by ${fmtCurrency(prev.income - current.income)} vs previous month.`);
    }

    return list;
  }, [current, prev, budget, cat, kpis, month, fmtCurrency]);

  // Loading / empty
  const isLoading = isRollupsLoading || isCatLoading || isTrendsLoading;
  const hasData = Boolean(current);
  const activeTabLoading = tab === 'overview'
    ? (isRollupsLoading || isBvaLoading || isSavingsLoading)
    : tab === 'categories'
      ? (isCatLoading || isRollupsLoading)
      : isTrendsLoading;

  const tabLabelMap: Record<'overview' | 'categories' | 'trends', string> = {
    overview: 'Overview',
    categories: 'Categories',
    trends: 'Trends',
  };

  const handleExportPdf = async () => {
    if (!exportRef.current) {
      setExportError('Export failed: report content is not ready yet.');
      return;
    }
    setExportError(null);
    setIsExporting(true);
    try {
      const filename = buildReportsPdfFilename({ month, tab });
      await exportReportsViewToPdf({
        node: exportRef.current,
        filename,
      });
    } catch (err) {
      console.error('Reports PDF export error:', err);
      setExportError('Unable to export PDF. Please try again.');
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <Container maxWidth="xl" sx={{ py: { xs: 3, md: 5 } }}>
      <Stack spacing={{ xs: 2.5, md: 3 }}>
        <Paper
          sx={{
            p: { xs: 2.5, sm: 3, md: 4 },
            borderRadius: 4,
            border: '1px solid',
            borderColor: 'divider',
            background: 'linear-gradient(135deg, rgba(25, 118, 210, 0.08) 0%, rgba(255, 255, 255, 0.98) 55%, rgba(46, 125, 50, 0.08) 100%)',
            boxShadow: '0 18px 40px rgba(15, 23, 42, 0.06)',
          }}
        >
          <Stack spacing={1.25} sx={{ maxWidth: 820 }}>
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 1,
                px: 1.25,
                py: 0.75,
                borderRadius: 999,
                bgcolor: 'rgba(25, 118, 210, 0.10)',
                color: 'primary.main',
                width: 'fit-content',
              }}
            >
              <Typography variant="body2" fontWeight={700}>
                Reporting workspace
              </Typography>
            </Box>
            <Typography variant="h4" sx={{ fontWeight: 700, letterSpacing: '-0.02em' }}>
              Reports & Trends
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.6 }}>
              Review performance, compare periods, and export a clear snapshot of your financial trends.
            </Typography>
          </Stack>
        </Paper>

        <Paper
          sx={{
            p: { xs: 2, sm: 2.5, md: 3 },
            borderRadius: 3,
            border: '1px solid',
            borderColor: 'divider',
            boxShadow: '0 10px 28px rgba(15, 23, 42, 0.05)',
          }}
        >
          <Stack spacing={2}>
            <Box>
              <Typography variant="overline" color="primary.main" sx={{ fontWeight: 700, letterSpacing: '0.08em' }}>
                Controls
              </Typography>
              <Typography variant="h6" sx={{ mt: 0.25, mb: 0.75, fontWeight: 700 }}>
                Report Controls
              </Typography>
            </Box>

            <Stack direction={{ xs:'column', md:'row' }} spacing={2} alignItems={{ md:'center' }} justifyContent="space-between">
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={2}
                alignItems={{ sm: 'center' }}
                sx={{ minWidth: 0, flex: 1 }}
              >
                <MonthPicker
                  value={month}
                  onChange={setMonth}
                  fullWidth
                  sx={{ width: { xs: '100%', sm: 'auto' }, minWidth: { sm: 170 }, maxWidth: { sm: 220 } }}
                />
                <Tabs
                  value={tab}
                  onChange={(_,v)=>setTab(v)}
                  aria-label="reports tabs"
                  variant="scrollable"
                  scrollButtons="auto"
                  allowScrollButtonsMobile
                  sx={{ minWidth: 0 }}
                >
                  <Tab value="overview"   label="Overview" />
                  <Tab value="categories" label="Categories" />
                  <Tab value="trends"     label="Trends" />
                </Tabs>
              </Stack>
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={1}
                alignItems={{ xs: 'stretch', sm: 'center' }}
                justifyContent="flex-end"
                flexWrap="wrap"
              >
                <FormControlLabel
                  control={<Switch checked={includeTentative} onChange={(e)=>setIncludeTentative(e.target.checked)} />}
                  label="Include Tentative"
                  sx={{ mr: { xs: 0, sm: 1 } }}
                />
                <FormControlLabel
                  control={<Switch checked={comparePrev} onChange={(e)=>setComparePrev(e.target.checked)} />}
                  label="Compare Previous Month"
                  sx={{ mr: { xs: 0, sm: 1 } }}
                />
                <Button
                  variant="contained"
                  onClick={handleExportPdf}
                  disabled={isExporting || activeTabLoading}
                  sx={{ width: { xs: '100%', sm: 'auto' } }}
                >
                  {isExporting ? 'Exporting...' : 'Export PDF'}
                </Button>
              </Stack>
            </Stack>
          </Stack>
        </Paper>

        {exportError ? (
          <Alert severity="error">{exportError}</Alert>
        ) : null}

        <Box
          ref={exportRef}
          sx={{
            backgroundColor: '#fff',
            p: { xs: 1.5, sm: 2, md: 2.5 },
            borderRadius: 3,
            border: '1px solid',
            borderColor: 'divider',
            boxShadow: '0 10px 28px rgba(15, 23, 42, 0.05)',
          }}
        >
          <Stack spacing={2}>
            <Paper sx={{ p: { xs: 2, sm: 2.5 }, borderRadius: 2.5, border: '1px solid', borderColor: 'divider', boxShadow: 'none' }}>
              <Typography variant="overline" color="primary.main" sx={{ fontWeight: 700, letterSpacing: '0.08em' }}>
                Snapshot
              </Typography>
              <Typography variant="h5" sx={{ fontWeight: 700 }}>Velora Reports</Typography>
              <Typography variant="body2" sx={{ opacity: 0.85 }}>
                Month: {month} | Tab: {tabLabelMap[tab]}
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.85 }}>
                Include Tentative: {includeTentative ? 'Yes' : 'No'} | Compare Previous Month: {comparePrev ? 'Yes' : 'No'}
              </Typography>
            </Paper>

            <Paper sx={{ p: { xs: 2, sm: 2.5 }, borderRadius: 2.5, border: '1px solid', borderColor: 'divider', boxShadow: 'none' }}>
              <Typography variant="overline" color="primary.main" sx={{ fontWeight: 700, letterSpacing: '0.08em' }}>
                KPI summary
              </Typography>
              <Box sx={{ mt: 1.25 }}>
                <SummaryStrip
                  loading={isLoading}
                  month={month}
                  kpis={kpis}
                  budget={budget}
                  savings={savings?.savings}
                />
              </Box>
            </Paper>

            <Paper sx={{ p: { xs: 2, sm: 2.5 }, borderRadius: 2.5, border: '1px solid', borderColor: 'divider', boxShadow: 'none' }}>
              <Typography variant="h6" gutterBottom>Insights</Typography>
              <InsightsList loading={isLoading} insights={insights} hasData={hasData} />
            </Paper>

            {tab === 'overview' && (
              <Paper sx={{ p:{ xs: 2, sm: 2.5 }, borderRadius: 2.5, border: '1px solid', borderColor: 'divider', boxShadow: 'none' }}>
                <Typography variant="h6" sx={{ mb: 1 }}>Monthly Summary</Typography>
                <MonthlySummaryChart
                  data={rollups ?? []}
                  comparePrev={comparePrev}
                  month={month}
                  bvaSeries={bva?.series ?? []}
                  savings={savings?.savings}
                />
              </Paper>
            )}

            {tab === 'categories' && (
              <Stack direction={{ xs:'column', md:'row' }} spacing={2}>
                <Paper sx={{ p:2, flex: 1, borderRadius: 2.5, border: '1px solid', borderColor: 'divider', boxShadow: 'none' }}>
                  <Typography variant="h6">Category Breakdown</Typography>
                  <Divider sx={{ my:1 }} />
                  <Box sx={{ px: { xs: 0, sm: 0.5 } }}>
                    <CategorySpendPie data={cat?.items ?? []} />
                  </Box>
                </Paper>
                <Paper sx={{ p:2, flex: 1, borderRadius: 2.5, border: '1px solid', borderColor: 'divider', boxShadow: 'none' }}>
                  <Typography variant="h6">Top Categories</Typography>
                  <Divider sx={{ my:1 }} />
                  <Box sx={{ px: { xs: 0, sm: 0.5 }, pt: 0.5 }}>
                    <CategoryTable
                      month={month}
                      breakdown={cat?.items ?? []}
                      prev={prev}
                      current={current}
                    />
                  </Box>
                </Paper>
              </Stack>
            )}

            {tab === 'trends' && (
              <Paper sx={{ p:2, borderRadius: 2.5, border: '1px solid', borderColor: 'divider', boxShadow: 'none' }}>
                <Typography variant="h6">Trends (12 mo)</Typography>
                <Divider sx={{ my:1 }} />
                <TrendsSparkline data={trends ?? []} />
              </Paper>
            )}
          </Stack>
        </Box>
      </Stack>
    </Container>
  );
};

export default Reports;
